import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormField,
  Header,
  Select,
  SelectProps,
  SpaceBetween,
} from "@amzn/awsui-components-console";

import type { AtozPreScreenData } from "./types";

interface AtozPreScreenProps {
  onPreScreenSubmit: (data: AtozPreScreenData) => void;
}

const COUNTRY_OPTIONS: SelectProps.Option[] = [
  { label: "United States", value: "United States" },
  { label: "Canada", value: "Canada" },
  { label: "EU/UK", value: "EU/UK" },
];

/**
 * AtoZ pre-screen rendered by `JsonFormsClaimIntakeApp` for the `atoz` LOB.
 *
 * Presents a single country dropdown. Once a country is selected and the
 * user clicks Continue, the schema `atoz` + selected country is resolved.
 */
function AtozPreScreen({ onPreScreenSubmit }: AtozPreScreenProps): JSX.Element {
  const [selectedCountry, setSelectedCountry] = useState<SelectProps.Option | null>(null);

  const handleCountryChange = useCallback(({ detail }: { detail: SelectProps.ChangeDetail }) => {
    setSelectedCountry(detail.selectedOption);
  }, []);

  const handleSubmit = useCallback(() => {
    if (selectedCountry?.value) {
      onPreScreenSubmit({ country: selectedCountry.value });
    }
  }, [selectedCountry, onPreScreenSubmit]);

  const isSubmitDisabled = useMemo(() => !selectedCountry?.value, [selectedCountry]);

  return (
    <Box padding="l" data-testid="atoz-pre-screen">
      <SpaceBetween size="m" direction="vertical">
        <Header variant="h2">AtoZ claim intake</Header>

        <FormField
          label="Country"
          description="Select the country for this claim."
          data-testid="atoz-pre-screen-country-form-field"
        >
          <Select
            data-testid="atoz-pre-screen-country-select"
            options={COUNTRY_OPTIONS}
            selectedOption={selectedCountry}
            onChange={handleCountryChange}
            placeholder="Select a country"
          />
        </FormField>

        <Button
          variant="primary"
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          data-testid="atoz-pre-screen-submit-button"
        >
          Continue
        </Button>
      </SpaceBetween>
    </Box>
  );
}

export default AtozPreScreen;
